import {
  differenceInCalendarDays,
  differenceInMinutes,
  format,
  formatDistanceToNowStrict,
  isValid,
  parseISO,
} from 'date-fns'
import { DateTime } from 'luxon'

const SHORT_DATE_FORMAT = 'MM/dd/yyyy'
const LOCAL_DATE_FORMAT = 'yyyy-MM-dd'
export const MOMENTS_AGO = 'A few moments ago'
export const SHORT_MOMENTS_AGO = 'Moments ago'
const DAYS_IN_WEEK = 7
const MINUTES_IN_MOMENT = 5

export type LocalDateOptions = {
  timeZone?: string
}

const getLocalDateTime = (date: Date, options?: LocalDateOptions) =>
  DateTime.fromJSDate(date, options?.timeZone ? { zone: options.timeZone } : undefined)

export function getLocalDateParts(date: Date, options?: LocalDateOptions) {
  const localDate = getLocalDateTime(date, options)

  return {
    year: localDate.year,
    month: localDate.month,
    day: localDate.day,
  }
}

export function formatLocalDate(date: Date, options?: LocalDateOptions): string {
  return getLocalDateTime(date, options).toFormat(LOCAL_DATE_FORMAT)
}

export function getCurrentLocalDateString(date: Date = new Date(), options?: LocalDateOptions): string {
  return formatLocalDate(date, options)
}

export function getMonthDateRange(date: Date, options?: LocalDateOptions) {
  const month = getLocalDateTime(date, options).startOf('month')

  return {
    period: month.toFormat(LOCAL_DATE_FORMAT),
    startDate: month.toFormat(LOCAL_DATE_FORMAT),
    endDate: month.endOf('month').toFormat(LOCAL_DATE_FORMAT),
  }
}

export function getMonthDateKey(date: Date, day: number, options?: LocalDateOptions): string {
  return getLocalDateTime(date, options).startOf('month').set({ day }).toFormat(LOCAL_DATE_FORMAT)
}

export function friendlyFormatISODate(isoDate: string, options?: { short?: boolean }): string {
  const now = new Date()
  const date = parseISO(isoDate)
  const isShortFormat = options?.short

  if (!isValid(date)) {
    return ''
  }

  const diffMins = differenceInMinutes(now, date, { roundingMethod: 'floor' })
  if (diffMins < MINUTES_IN_MOMENT) {
    return isShortFormat ? SHORT_MOMENTS_AGO : MOMENTS_AGO
  }

  if (differenceInCalendarDays(now, date) < DAYS_IN_WEEK) {
    const distance = formatDistanceToNowStrict(date, { addSuffix: true, roundingMethod: 'floor' })
    return isShortFormat
      ? distance
          .replace(/ days?/, 'd')
          .replace(/ hours?/, 'h')
          .replace(/ minutes?/, 'm')
          .replace(/ seconds?/, 's')
      : distance
  }

  return format(date, SHORT_DATE_FORMAT)
}

export function fullDateFormat(isoDate: string) {
  const date = new Date(isoDate)

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date)
}
